<template>
  <div id="app">
    <div v-if="!restodirectory()">
      <!-- We are hiding the web app nav to display restaurant directory content -->
      <LoggedInHeader v-show="isLoggedIn()" />

      <GuestHeader
        v-if="
          guestUser &&
            !isLoggedIn() &&
            !isConfirmationPage() &&
            !isLinkExpiryPage()
        "
      />
    </div>

    <router-view> </router-view>

    <div v-if="guestUser">
      <AddCardModal />
    </div>

    <div v-if="getFirstEmployeeId">
      <AddCardModal />
      <OrderDetailModal />
      <ReferralModal />
      <AdminAddUserModal />
      <EditBulkUsersModal />
      <AdminEditUserModal />
      <PlaceOrderDetailModal />
      <AddTeamModal />
      <AssignToTeamModal />
      <AddAdminModal />
      <DefaultOfficeSelectionModal />
    </div>

    <Footer v-if="isLoggedIn()" />

    <GuestFooter v-if="guestUser && !isLoggedIn() && !isLinkExpiryPage()" />

    <div class="layer"></div>
  </div>
</template>

<script>
import Footer from "./components/Footer";
import GuestFooter from "./components/GuestFooter";
import GuestHeader from "./components/GuestHeader";
import Header from "./components/Header";
import LoggedInHeader from "./components/LoggedInHeader";
import AddCardModal from "./components/AddCardModal";
import OrderDetailModal from "./components/OrderDetailModal";
import { mapGetters, mapState } from "vuex";
import ReferralModal from "./components/ReferralModal.vue";
import AdminAddUserModal from "./components/OfficeAdmin/AdminAddUserModal";
import EditBulkUsersModal from "./components/OfficeAdmin/EditBulkUsersModal.vue";
import AdminEditUserModal from "./components/OfficeAdmin/AdminEditUserModal";
import PlaceOrderDetailModal from "./components/OfficeAdmin/PlaceOrder/PlaceOrderDetailModal.vue";
import AddTeamModal from "./components/OfficeAdmin/Teams/AddTeamModal.vue";
import AssignToTeamModal from "./components/OfficeAdmin/Teams/AssignToTeamModal.vue";
import AddAdminModal from "./components/OfficeAdmin/Teams/AddAdminModal.vue";
import DefaultOfficeSelectionModal from "./components/DefaultOfficeSelectionModal.vue";

import {
  isLoggedIn,
  getAccessToken,
  setPKToken,
  clearPKToken,
  logOut
} from "./utils/api";
import "./assets/js/base";
import * as Sentry from "@sentry/browser";

export default {
  name: "App",
  metaInfo: {
    // if no subcomponents specify a metaInfo.title, this title will be used
    title: "Uncatering by hungerhub: Employee Meal Ordering",
    // all titles will be injected into this template
    titleTemplate: "%s"
  },
  data() {
    return {
      user_name: ""
    };
  },
  Sentry,
  components: {
    Footer,
    Header,
    LoggedInHeader,
    AddCardModal,
    OrderDetailModal,
    ReferralModal,
    AdminAddUserModal,
    EditBulkUsersModal,
    AdminEditUserModal,
    PlaceOrderDetailModal,
    DefaultOfficeSelectionModal,
    AddTeamModal,
    AssignToTeamModal,
    AddAdminModal,
    GuestFooter,
    GuestHeader
  },
  created() {
    clearPKToken();
    if (!this.$route.query.pk) {
      getAccessToken();
    }
    if (!this.$route.meta.title) {
      document.title = "hungerhub - Uncatering";
    }
  },
  computed: {
    ...mapState(["currentEmp", "currentOrganization", "employee"]),
    ...mapGetters(["getAlert", "getAlertMessage", "getFirstEmployeeId"]),
    guestUser() {
      return this.$route.path.includes("/guest");
    }
  },
  watch: {
    currentEmp: function(emp) {
      if (
        this.$router.currentRoute.meta.officeAdmin &&
        emp &&
        emp.role != "admin" &&
        !emp.team_admin
      ) {
        this.$router.push("/restaurants").catch(err => {});
      }
    },
    currentOrganization: function(currentOrg) {
      if (
        currentOrg &&
        currentOrg.attributes &&
        currentOrg.attributes.soft_deleted
      ) {
        this.$router.push("/orders").catch(err => {});
      }
    },
    getAlertMessage: function(val) {
      if (
        this.$router.currentRoute.meta.officeAdmin &&
        val == "user not authorized"
      ) {
        this.$router.push("/restaurants").catch(err => {});
      }
    },
    getAlert: function(val) {
      if (val) {
        this.$swal({
          title: this.$router.currentRoute.meta.officeAdmin
            ? this.getAlertMessage == "user not authorized"
              ? "Admin functionality has been removed from this account."
              : this.getAlertMessage
            : this.getAlertMessage == "user not authorized"
            ? "You have been removed from this account. Please contact your account administrator or start a live chat if you require further assistance."
            : this.getAlertMessage
        });
        if (
          this.getAlertMessage == "Access token expired" ||
          this.getAlertMessage == "user not authorized"
        ) {
          logOut(this);
        }
        this.$store.commit("SHOW_ALERT", {
          value: false,
          msg: ""
        });
      }
    }
  },
  mounted() {
    // Freshchat settings

    let employee = this.$store.state.employee;

    Sentry.configureScope(scope => {
      scope.setUser({ email: employee.email });
    });

    // Initialize Freshchat
    if (!window.__freshchat_initialized__) {
      window.fcWidget.init({
        token: "6ecb7260-752a-4aa9-b407-cb213479630e",
        host: "https://wchat.freshchat.com",
        tags: ["loggedout"],
        externalId: employee?.email,
        config: {
          headerProperty: {
            direction: "ltr"
          }
        }
      });

      window.__freshchat_initialized__ = true;
    }

    window.fcWidget.user.setProperties({
      firstName: employee?.first_name,
      lastName: employee?.last_name,
      email: employee?.email
    });

    if (this.$route.query.pk && this.$route.query.key) {
      this.$store.dispatch("SET_ADMIN_LOGIN");
      this.handleAdminLogin();
    }

    this.$store.commit("SET_ORDERS_DATE_OFFICE_ADMIN", new Date());

    if (this.getFirstEmployeeId)
      this.$store.dispatch("FETCH_EMPLOYEE_PROFILE", {
        empId: this.getFirstEmployeeId
      });
  },
  methods: {
    isLoggedIn() {
      return isLoggedIn();
    },
    restodirectory() {
      if (
        this.$route.name === "RestaurantDirectory" ||
        this.$route.name === "RestaurantDetailsPage"
      ) {
        return true;
      } else {
        return false;
      }
    },
    handleAdminLogin() {
      let pk = this.$route.query.pk;
      setPKToken(pk);

      if (isLoggedIn()) {
        this.$router.push("/restaurants").catch(err => {});
      } else {
        alert("Admin log in failed. Please try again");
        this.$router.push({ name: "Login" });
      }
    },
    isConfirmationPage() {
      return this.$route.path.startsWith("/guest/confirmation");
    },
    isLinkExpiryPage() {
      return this.$route.path.startsWith("/guest/link-expired");
    }
  },

  beforeDestroy() {
    localStorage.removeItem("pk");
  }
};
</script>

<style lang="scss">
@import "./assets/styles/base";
</style>



// WEBPACK FOOTER //
// src/App.vue